import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Button } from '@/components/Button/Button';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { FollowBanner } from '@/features/session/FollowBanner/FollowBanner';
import { useFollowMode } from '@/features/session/useFollowMode';
import { ModuleNav } from '@/features/viewer/components/ModuleNav/ModuleNav';
import { ProgressCard } from '@/features/viewer/components/ProgressCard/ProgressCard';
import { TopicPanel, type ViewMode } from '@/features/viewer/components/TopicPanel/TopicPanel';
import { useWorkshopContent } from '@/features/viewer/hooks/useWorkshopContent';
import {
  useWorkshopNavigation,
  type Position,
} from '@/features/viewer/hooks/useWorkshopNavigation';
import type { WorkshopManifest } from '@/lib/api/types';
import { pluralise } from '@/lib/format';
import styles from './ViewerPage.module.scss';

export function ViewerPage() {
  const [params] = useSearchParams();
  // Present when the attendee opened the link for a live cohort; absent for the
  // standalone/self-paced viewer.
  const cohortId = params.get('cohort');
  const { state, reload } = useWorkshopContent(cohortId);

  return (
    <AsyncBoundary state={state} subject="the workshop" onRetry={reload}>
      {(manifest) => <Workshop manifest={manifest} cohortId={cohortId} />}
    </AsyncBoundary>
  );
}

function Workshop({ manifest, cohortId }: { manifest: WorkshopManifest; cohortId: string | null }) {
  const [mode, setMode] = useState<ViewMode>('facilitator');
  const navigation = useWorkshopNavigation(manifest);
  const { goTo } = navigation;

  const applyPresenterPosition = useCallback((position: Position) => goTo(position), [goTo]);
  const follow = useFollowMode({ cohortId, onPosition: applyPresenterPosition });

  // An attendee navigating by hand has stopped following (US-4.3); the banner
  // says so and offers one action to rejoin.
  const withPause = (action: () => void) => () => {
    if (follow.following) follow.pause();
    action();
  };

  if (!navigation.module || !navigation.topic) {
    return (
      <EmptyState
        title="This workshop has no content yet"
        description="No modules were published for this workshop. An admin needs to sync and publish a source version before it can be delivered."
      />
    );
  }

  const topicCount = navigation.module.topics.length;

  return (
    <>
      <FollowBanner follow={follow} />

      <section className={styles.intro}>
        <div>
          <p className={styles.eyebrow}>Workshop viewer</p>
          <h1>{manifest.title}</h1>
          <p className={styles.lede}>
            A focused space for delivering and following this workshop, module by module.
          </p>
          <Button
            className={styles.viewToggle}
            onClick={() => setMode((current) => (current === 'participant' ? 'facilitator' : 'participant'))}
          >
            {mode === 'participant' ? 'Facilitator view' : 'Participant view'}
          </Button>
        </div>

        <ProgressCard
          value={navigation.progress}
          label={navigation.isLast ? 'Final topic' : 'In progress'}
          hint={`${pluralise(manifest.modules.length, 'module')} · ${pluralise(topicCount, 'topic')} in this module`}
        />
      </section>

      <div className={styles.grid}>
        <div className={styles.sidebar}>
          <ModuleNav
            modules={manifest.modules}
            activeModule={navigation.position.module}
            onSelect={(index) => withPause(() => navigation.goToModule(index))()}
          />
        </div>

        <div>
          <TopicPanel
            topic={navigation.topic}
            mode={mode}
            moduleIndex={navigation.position.module}
            topicIndex={navigation.position.topic}
            topicCount={topicCount}
            repoLink={mode === 'facilitator' ? manifest.repoLink : undefined}
          />

          <div className={styles.pager}>
            <Button onClick={withPause(navigation.previous)} disabled={navigation.isFirst}>
              ← Previous
            </Button>
            <Button variant="primary" onClick={withPause(navigation.next)} disabled={navigation.isLast}>
              {navigation.isLast ? 'Workshop complete ✓' : 'Next topic →'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
